import { defineStore } from "pinia";

const ORDER_LABELS = {
    name: "Name (A–Z)",
    rating: "Rating",
};

function defaultSelections() {
    return {
        category: null,
        country: null,
        city: null,
        order: "name",
    };
}

export const useActivityStore = defineStore("activity", {
    state: () => ({
        categories: [],
        countries: [],
        cities: [],
        selections: defaultSelections(),
        activities: [],
        page: 1,
        lastPage: 1,
        total: 0,
        loading: false,
        hasFetched: false,
        metaLoaded: false,
        error: null,
        featuredCarousel: null,
        featuredCarouselLoaded: false,
    }),

    getters: {
        categoryOptions: (state) => [
            { value: null, label: "All" },
            ...state.categories.map((c) => ({ value: c.id, label: c.name })),
        ],

        countryOptions: (state) => [
            { value: null, label: "All countries" },
            ...state.countries.map((c) => ({ value: c.id, label: c.name })),
        ],

        // Cascades from the selected country; "All cities" until a country is chosen.
        cityOptions: (state) => {
            const cities = state.selections.country
                ? state.cities.filter(
                      (c) => c.country_id === state.selections.country,
                  )
                : [];
            return [
                { value: null, label: "All cities" },
                ...cities.map((c) => ({ value: c.id, label: c.name })),
            ];
        },

        orderOptions: () =>
            Object.entries(ORDER_LABELS).map(([value, label]) => ({
                value,
                label,
            })),

        hasMore: (state) => state.page < state.lastPage,

        activeCount: (state) => {
            const defaults = defaultSelections();
            return Object.keys(defaults).filter(
                (key) => state.selections[key] !== defaults[key],
            ).length;
        },
    },

    actions: {
        async loadFeaturedCarousel() {
            if (this.featuredCarouselLoaded) return;
            try {
                const { data } = await axios.get("/api/featured-carousel");
                this.featuredCarousel = data || null;
            } catch (e) {
                console.error("Failed to load featured carousel", e);
            } finally {
                this.featuredCarouselLoaded = true;
            }
        },

        async setup() {
            if (this.metaLoaded) return;
            try {
                const { data } = await axios.get("/api/activities/filters");
                this.categories = data.categories ?? [];
                this.countries = data.countries ?? [];
                this.cities = data.cities ?? [];
                this.metaLoaded = true;
            } catch (e) {
                console.error("Failed to load activity filters", e);
                this.error = "Could not load filters.";
            }
            await this.fetchActivities();
        },

        _params(page) {
            const { category, country, city, order } = this.selections;
            const params = { order, page };
            if (category) params.category = category;
            if (country) params.country = country;
            if (city) params.city = city;
            return params;
        },

        async fetchActivities() {
            this.loading = true;
            this.error = null;
            // Clear immediately so the loading state shows the filters are changing.
            this.activities = [];
            try {
                const { data } = await axios.get("/api/activities", {
                    params: this._params(1),
                });
                this.activities = data.data ?? [];
                this.page = data.current_page ?? 1;
                this.lastPage = data.last_page ?? 1;
                this.total = data.total ?? 0;
            } catch (e) {
                console.error("Failed to load activities", e);
                this.error = "Could not load activities.";
            } finally {
                this.loading = false;
                this.hasFetched = true;
            }
        },

        async loadMore() {
            if (this.loading || !this.hasMore) return;
            this.loading = true;
            try {
                const { data } = await axios.get("/api/activities", {
                    params: this._params(this.page + 1),
                });
                this.activities.push(...(data.data ?? []));
                this.page = data.current_page ?? this.page;
                this.lastPage = data.last_page ?? this.lastPage;
            } catch (e) {
                console.error("Failed to load more activities", e);
            } finally {
                this.loading = false;
            }
        },

        setFilter(key, value) {
            if (this.selections[key] === value) return;
            this.selections[key] = value;
            // Changing country invalidates the cascaded city selection.
            if (key === "country") this.selections.city = null;
            this.fetchActivities();
        },

        reset() {
            this.selections = defaultSelections();
            this.fetchActivities();
        },
    },
});
